import {
    ChangeDetectionStrategy,
    Component,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { Setting } from '@app-state';
import {
    ClientErrorCodes,
    enumMapper,
    globalStateFilter,
    ITriggerGrid,
    TRIGGER_TYPE_FILTER,
    TriggerType,
} from '@models';
import {
    GRID_SERVICE,
    ODataGridComponent,
    STATE_HANDLER,
    urlStateHandler,
} from '@pages/generics/grid';
import { TriggersGridService } from '@services';

import { BehaviorSubject } from 'rxjs';
import {
    finalize,
    takeUntil,
} from 'rxjs/operators';

@Component({
    templateUrl: './triggers.page.html',
    styleUrls: ['./triggers.page.scss'],
    providers: [{
        provide: GRID_SERVICE,
        useClass: TriggersGridService,
    },
    {
        provide: STATE_HANDLER,
        useValue: urlStateHandler,
    }],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class TriggersPageComponent extends ODataGridComponent<ITriggerGrid, number> implements OnInit {
    public loading$ = new BehaviorSubject(false);

    public statusList = globalStateFilter();
    public typeList = enumMapper(TriggerType, {
        resourceKey: 'trigger_type',
        filterValueMap: TRIGGER_TYPE_FILTER,
    });

    public get currentTimezone(): string {
        return this._appState.settings.get(Setting.timezone)!;
    }

    public hasQueuesViewPermission$ = this._appState.user.permissions
        .isGranted('Queues.View');
    public hasRobotsViewPermission$ = this._appState.user.permissions
        .isGranted('Robots.View');

    private _triggersService = this._service as TriggersGridService;

    ngOnInit() {
        this._appState.language.change$
            .pipe(
                takeUntil(this._destroyed$),
            )
            .subscribe(() => this._refresh$.next());
    }

    public get isEverySelectedEntryDisabled(): boolean {
        return !this._grid.selectionManager.selected.every(e => !e.enabled);
    }

    public get isEverySelectedEntryEnabled(): boolean {
        return !this._grid.selectionManager.selected.every(e => e.enabled);
    }

    public setState(enabled: boolean, triggerId?: number) {
        const triggerIds = triggerId ?
            [triggerId] :
            this._grid.selectionManager.selected.map(e => e.id);

        if (!triggerIds.length) { return; }

        this.loading$.next(true);

        this._triggersService.setEnabled(triggerIds, enabled)
            .pipe(
                finalize(() => {
                    this._refresh$.next();
                    this.loading$.next(false);
                }),
            )
            .subscribe((res: any) => {
                if (enabled && !res.value) {
                    this._snack.error(
                        `${
                        this._translate.instant('triggers_not_all_triggers_enabled_error')
                        } (#${
                        ClientErrorCodes.TriggersNotAllEnabled
                        })`,
                    );
                }
            });
    }
}
